import { parseQuantityConditional, splitConditionalExpression, stripOuterParentheses } from "./quantityRuleUtils";
import { splitVectorParts, unwrapVector } from "./vectorRuleUtils";

export const OPERATORS = ["+", "-", "*", "/", "%"];
export const SETTING_MODES = ["", "cur", "sum", "len", "idx", "idx0", "bef.sum", "aft.sum"];

export function createOperand(type = "number", value = "0") {
  return { type, value };
}

export function createExpression() {
  return { negative: false, first: createOperand(), operations: [] };
}

export function createVector() {
  return {
    x: createExpression(),
    y: createExpression(),
    z: createExpression()
  };
}

export function createPositionRule() {
  return {
    condition: null,
    whenTrue: [createVector()],
    whenFalse: []
  };
}

export function compileOperand(operand) {
  if (operand.type === "setting") return `(*${operand.value}${operand.mode ? `.${operand.mode}` : ""}*)`;
  if (operand.type === "dimension") return operand.value || "width";
  if (operand.type === "text") return JSON.stringify(operand.value);
  if (operand.type === "expression") return operand.value || "0";
  const value = Number(operand.value);
  return Number.isFinite(value) ? String(value) : "0";
}

export function compileExpression(expression) {
  const first = compileOperand(expression.first);
  const body = expression.operations.reduce(
    (result, operation) =>
      `${result}${operation.operator}${compileOperand(operation.operand)}`,
    first
  );
  return expression.negative ? `-(${body})` : body;
}

export function compileVector(vector) {
  return `[${compileExpression(vector.x)},${compileExpression(
    vector.y
  )},${compileExpression(vector.z)}]`;
}

export function compileVectorList(vectors) {
  return `[${vectors.map(compileVector).join(",")}]`;
}

export function compilePositionRule(rule) {
  const whenTrue = compileBranch(rule.whenTrue);
  if (!rule.condition) return whenTrue;

  const condition = rule.condition.left ? `${compileExpression(rule.condition.left)} ${rule.condition.operator} ${compileExpression(rule.condition.right)}` : `(*${rule.condition.settingId}*)${
    rule.condition.operator
  }${JSON.stringify(rule.condition.value)}`;
  const whenFalse = compileBranch(Array.isArray(rule.whenFalse) && !rule.whenFalse.length ? rule.whenTrue : rule.whenFalse);
  return `(${condition}) ? (${whenTrue}) : (${whenFalse})`;
}

export function compileBranch(branch) {
  return Array.isArray(branch) ? compileVectorList(branch) : compilePositionRule(branch);
}

export function createCondition() {
  return { left: createExpression(), operator: "==", right: createExpression() };
}

function parseOperand(value) {
  const source = value.trim();
  const setting = source.match(/^\(\*(\d+(?:@\d+)?)(?:\.(cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)$/);
  if (setting) return { type: "setting", value: setting[1], mode: setting[2] || "" };
  if (["width", "height", "depth", "qty"].includes(source)) return createOperand("dimension", source);
  if (source !== "" && Number.isFinite(Number(source))) return createOperand("number", source);
  if (source.startsWith('"')) {
    try { const text = JSON.parse(source); if (typeof text === "string") return createOperand("text", text); } catch {}
  }
  return createOperand("expression", source);
}

export function parseExpression(value) {
  const source = String(value).trim();
  // Preserve complex/grouped expressions verbatim; simple operands retain their selectors.
  const first = parseOperand(source);
  return { ...createExpression(), first };
}

export function parsePositionRule(value) {
  const parseNode = (value) => {
    let source = stripOuterParentheses(value);
    const inner = unwrapVector(source);
    if (inner !== source && (inner.trim().startsWith("[") || (splitVectorParts(inner).length === 1 && splitConditionalExpression(inner)))) source = inner;
    const conditional = splitConditionalExpression(source);
    if (conditional) {
      const comparison = parseQuantityConditional(source);
      // A bare boolean condition remains a boolean expression.
      const explicitComparison = /(?:===|!==|==|!=|>=|<=|>|<)/.test(conditional.condition);
      return {
        condition: {
          left: parseExpression(explicitComparison ? comparison.left : conditional.condition),
          operator: explicitComparison ? comparison.relation : "!=",
          right: parseExpression(explicitComparison ? comparison.right : "0")
        },
        whenTrue: parseNode(conditional.whenTrue),
        whenFalse: parseNode(conditional.whenFalse)
      };
    }
    const entries = splitVectorParts(source);
    if (entries.every((entry) => entry.startsWith("["))) {
      return entries.flatMap((entry) => {
        const axes = splitVectorParts(unwrapVector(entry));
        if (axes.length !== 3 || axes.some((axis) => !axis)) throw new Error("Három tengelyérték szükséges.");
        return [{ x: parseExpression(axes[0]), y: parseExpression(axes[1]), z: parseExpression(axes[2]) }];
      });
    }
    const axes = splitVectorParts(unwrapVector(source));
    if (axes.length !== 3 || axes.some((axis) => !axis)) throw new Error("Három tengelyérték szükséges.");
    return [{ x: parseExpression(axes[0]), y: parseExpression(axes[1]), z: parseExpression(axes[2]) }];
  };
  if (!String(value ?? "").trim()) return createPositionRule();
  // Check syntax without evaluating the user's formula.
  const syntax = String(value).replace(/\(\*\d+(?:@\d+)?(?:\.(?:cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)/g, "0");
  new Function(`return (${syntax})`); // eslint-disable-line no-new-func, no-new
  const parsed = parseNode(value);
  return Array.isArray(parsed) ? { condition: null, whenTrue: parsed, whenFalse: [] } : parsed;
}

export function validatePositionRule(rule) {
  const errors = [];
  if (Array.isArray(rule.whenTrue) && !rule.whenTrue.length) errors.push("Legalább egy pozíció szükséges.");
  if (rule.condition && !rule.condition.left && !rule.condition.settingId) {
    errors.push("A feltételhez válassz beállítást.");
  }

  const checkOperand = (operand) => {
    if (operand.type === "expression") {
      try {
        const syntax = operand.value.replace(/\(\*\d+(?:@\d+)?(?:\.(?:cur|sum|len|idx|idx0|bef\.sum|aft\.sum))?\*\)/g, "0");
        new Function(`return (${syntax})`); // eslint-disable-line no-new-func, no-new
      } catch { errors.push("Hibás kifejezés."); }
    }
    if (operand.type === "setting" && !operand.value) {
      errors.push("Egy képletben nincs kiválasztva a beállítás.");
    }
    if (operand.type === "number" && !Number.isFinite(Number(operand.value))) {
      errors.push("A konstans érték nem szám.");
    }
  };

  const checkExpression = (expression) => {
      checkOperand(expression.first);
      expression.operations.forEach((operation) => {
        checkOperand(operation.operand);
        if (
          operation.operator === "/" &&
          operation.operand.type === "number" &&
          Number(operation.operand.value) === 0
        ) {
          errors.push("Nullával osztás nem megengedett.");
        }
      });
  };
  if (rule.condition?.left) {
    checkExpression(rule.condition.left);
    checkExpression(rule.condition.right);
  }
  [rule.whenTrue, rule.whenFalse].forEach((branch) => {
    if (Array.isArray(branch)) branch.forEach((vector) => [vector.x, vector.y, vector.z].forEach(checkExpression));
    else errors.push(...validatePositionRule(branch));
  });

  if (
    rule.condition &&
    compileBranch(rule.whenTrue) ===
      compileBranch(Array.isArray(rule.whenFalse) && !rule.whenFalse.length ? rule.whenTrue : rule.whenFalse)
  ) {
    errors.push("Az igaz és hamis ág azonos; a feltétel elhagyható.");
  }
  return [...new Set(errors)];
}
